import { resolveUsersSentinel } from '@src/hooks/filters/useUsersFilter';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useUser } from '@src/hooks/useUser';
import {
  CARE_ORDER,
  type CareOrder,
  type MinimalSubscriptionResponse,
  type Quote,
  QUOTE,
  useCombinedErrorObs,
  useSubscription,
  useWSFilterPipe,
  wsScanToMap,
  wsStitchWith,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { asyncScheduler, map, type Observable, shareReplay, throttleTime } from 'rxjs';
import { calculateBestQuotes, type RfqBestQuoteBy } from '../../../utils/rfq';
import { InitiatingFromRow, ParentRFQIDRow, QuoteMarketRow, QuoteRow, type QuotesBlotterRow } from './types';
import { quotesV2ServerFilter, type QuotesV2TableFilter } from './useQuotesV2Filter';

function getQuoteKey(quote: Quote) {
  return quote.RFQID;
}

interface UseQuotesV2BlotterDataObsParams {
  filterFn: (quote: Quote) => boolean;
  filter: QuotesV2TableFilter;
  options: QuoteToRowsOptions;
}

export const useQuotesV2BlotterDataObs = ({ filterFn, filter, options }: UseQuotesV2BlotterDataObsParams) => {
  const { enrichRFQBlotterV2WithCareOrders } = useFeatureFlag();
  const { Name: currentUserName } = useUser();
  const serverFilter = useMemo(() => {
    const resolvedUsers = resolveUsersSentinel(filter.Users, currentUserName);
    return quotesV2ServerFilter({ ...filter, ...(resolvedUsers !== undefined ? { Users: resolvedUsers } : {}) });
  }, [filter, currentUserName]);

  const quotesRequest = useMemo(
    () => ({
      name: QUOTE,
      tag: 'useQuotesV2BlotterDataObs',
      ...serverFilter,
    }),
    [serverFilter]
  );

  const careOrdersRequest = useMemo(
    () => ({
      name: CARE_ORDER,
      tag: 'useQuotesV2BlotterDataObs',

      /* Start and End is good to add here for pagination purposes. Don't always need to load all COs */
      StartDate: serverFilter?.StartDate,
      EndDate: serverFilter?.EndDate,
    }),
    [serverFilter?.StartDate, serverFilter?.EndDate]
  );

  const { data: quotesObs, error: quotesError } = useSubscription<Quote>(quotesRequest);
  const { data: careOrdersObs, error: careOrdersError } = useSubscription<CareOrder>(
    enrichRFQBlotterV2WithCareOrders ? careOrdersRequest : null
  );

  const filterPipe = useWSFilterPipe({ getUniqueKey: getQuoteKey, filterFunc: filterFn });

  const enrichedQuotesObs = useMemo(
    () =>
      quotesObs.pipe(
        filterPipe,
        wsStitchWith({
          secondarySource: careOrdersObs,
          getPrimaryTypeKey: quote => quote.RFQID, // storage key for quotes is RFQID
          getPrimaryTypeStitchKey: quote => quote.ParentRFQID, // but we stitch (match) Quote<->CO using Quote.ParentRFQID <-> CO.OrderID
          getSecondaryTypeKey: co => co.OrderID,
          stitch: (quote, co) => quote.enrichWithCareOrder(co),
        }),
        wsScanToMap({ getUniqueKey: quote => quote.RFQID, newMapEachUpdate: false }),
        throttleTime(500, asyncScheduler, { leading: true, trailing: true }), // Streaming RFQs can be very spammy. Throttle a bit.
        shareReplay({ refCount: false, bufferSize: 1 })
      ),
    [filterPipe, quotesObs, careOrdersObs]
  );

  const dataObservable: Observable<MinimalSubscriptionResponse<QuotesBlotterRow>> = useMemo(() => {
    return enrichedQuotesObs.pipe(
      map(mapping => ({ initial: true, data: [...mapping.values()].flatMap(quote => quoteToRows(quote, options)) }))
    );
  }, [enrichedQuotesObs, options]);

  const errorObservable = useCombinedErrorObs([quotesError, careOrdersError]);
  return { dataObservable, errorObservable };
};

export interface QuoteToRowsOptions {
  groupByInitiatingFrom: boolean;
  groupByParentRFQID?: boolean;
  bestBy?: RfqBestQuoteBy;
}

function quoteToRows(quote: Quote, options: QuoteToRowsOptions): QuotesBlotterRow[] {
  let dataPath: string[] = [];
  const rows: QuotesBlotterRow[] = [];

  if (options.groupByParentRFQID) {
    dataPath = [...dataPath, ParentRFQIDRow.getPathComponent(quote)];
    rows.push(new ParentRFQIDRow([...dataPath], quote));
  }

  if (options.groupByInitiatingFrom) {
    dataPath = [...dataPath, InitiatingFromRow.getPathComponent(quote)];
    rows.push(new InitiatingFromRow([...dataPath], quote));
  }

  dataPath = [...dataPath, QuoteRow.getPathComponent(quote)];
  const quoteRow = new QuoteRow([...dataPath], quote, options.bestBy);

  const quoteMarketsWithBBO = calculateBestQuotes({
    quotes: quote.Markets,
    pricingMode: quote.PricingMode,
    bestBy: options.bestBy,
  });
  const quoteMarketRows = quoteMarketsWithBBO.map(quoteMarket => {
    return new QuoteMarketRow([...dataPath, QuoteMarketRow.getPathComponent(quote, quoteMarket)], quote, quoteMarket);
  });

  rows.push(quoteRow);
  quoteMarketRows.forEach(row => rows.push(row));
  return rows;
}
